import { useHistory, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import PageTitle from "../../layouts/PageTitle";
import { Form, Button, Row, Col, Alert, Spinner } from "react-bootstrap";
import DatePicker from "react-datepicker";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import {
    Save,
    X,
    Calculator,
    Calendar,
    FileText,
    AlertTriangle,
    CheckCircle,
    Plus,
    Trash2,
    Info,
    MessageSquare,
    Paperclip,
    Upload,
    File
} from 'lucide-react';
import "react-datepicker/dist/react-datepicker.css";
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from "../../../utils/ApiUtils";
import swal from "sweetalert";

import { toast } from 'sonner'

// Helper function to format numbers as Swedish currency (SEK)
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
};

const CreateVerification = () => {
    const { companyId } = useParams();
    const history = useHistory();

    // Initialize state variables for manual verification creation
    const [date, setDate] = useState(new Date());
    const [description, setDescription] = useState("");
    const [reference, setReference] = useState("");
    const [comments, setComments] = useState(""); // New state for comments
    const [attachments, setAttachments] = useState([]); // New state for attachments
    const [uploadingFiles, setUploadingFiles] = useState(false); // Loading state for file uploads

    // Preview-related state
    const [previewWindow, setPreviewWindow] = useState(null);
    const [hoveredAttachment, setHoveredAttachment] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    const [manualRows, setManualRows] = useState([
        { id: Date.now(), account_id: "", account_number: "", account_name: "", debit: "", credit: "" },
        { id: Date.now() + 1, account_id: "", account_number: "", account_name: "", debit: "", credit: "" }
    ]);
    const [accounts, setAccounts] = useState([]);
    const [balanceStatus, setBalanceStatus] = useState({ message: "", variant: "secondary" });
    const [loading, setLoading] = useState(false);
    const [savingVerification, setSavingVerification] = useState(false);
    const [error, setError] = useState(null);

    // Add the balance checking functions
    const areTransactionsBalanced = () => {
        if (!manualRows || manualRows.length === 0) {
            return false;
        }

        let totalDebit = 0;
        let totalCredit = 0;

        manualRows.forEach((row) => {
            const debitAmount = parseFloat(row.debit || 0);
            const creditAmount = parseFloat(row.credit || 0);

            totalDebit += debitAmount;
            totalCredit += creditAmount;
        });

        // Allow for small rounding differences (within 1 öre)
        const difference = Math.abs(totalDebit - totalCredit);
        return difference < 0.01;
    };

    // Enhanced form validation function that includes balance check
    const isFormValid = () => {
        // Check if description is provided
        if (!description || description.trim() === '') {
            return false;
        }

        // Check if there are transactions
        if (!manualRows || manualRows.length === 0) {
            return false;
        }

        // Check if any transaction is missing required data
        const hasInvalidTransaction = manualRows.some(row => {
            const hasAccount = row.account_id && row.account_number && row.account_number.trim() !== '';
            const hasAmount = (row.debit && parseFloat(row.debit) > 0) ||
                (row.credit && parseFloat(row.credit) > 0);
            return !hasAccount || !hasAmount;
        });

        if (hasInvalidTransaction) {
            return false;
        }

        // Check if transactions are balanced
        return areTransactionsBalanced();
    };

    const fetchAccounts = async () => {
        setLoading(true);
        setError(null);
        try {
            const accountsApiUrl = getApiUrl(`${companyId}/accounting/accounts?scope=company`);
            const response = await axios.get(accountsApiUrl, { withCredentials: true });

            if (response.data.accounts && Array.isArray(response.data.accounts)) {
                const formattedAccounts = response.data.accounts.map(account => ({
                    value: account.account_number,
                    label: `${account.account_number} - ${account.account_name}`,
                    accountNumber: account.account_number,
                    accountName: account.account_name,
                    accountType: account.account_type,
                    balance: account.balance || 0,
                    isActive: account.is_active !== false,
                    ...account
                }));

                formattedAccounts.sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));
                setAccounts(formattedAccounts);
            } else {
                throw new Error("Kunde inte hämta konton");
            }
        } catch (error) {
            console.error('Error fetching accounts:', error);
            setError('Det gick inte att hämta kontolistan. Försök igen senare.');
        } finally {
            setLoading(false);
        }
    };

    // Initialize the component - fetch accounts when component mounts
    useEffect(() => {
        fetchAccounts();
    }, [companyId]);

    // Calculate balance whenever rows change
    useEffect(() => {
        calculateBalance();
    }, [manualRows]);

    // Function to calculate and show balance status
    const calculateBalance = () => {
        let totalDebit = 0;
        let totalCredit = 0;

        manualRows.forEach((row) => {
            totalDebit += parseFloat(row.debit || 0);
            totalCredit += parseFloat(row.credit || 0);
        });

        const diff = Math.abs(totalDebit - totalCredit);

        if (diff < 0.01) { // Allow for rounding errors
            setBalanceStatus({
                message: "Debet och Kredit balanserar",
                variant: "success"
            });
        } else if (totalDebit > totalCredit) {
            setBalanceStatus({
                message: `Debet är mer än kredit med ${formatCurrency(totalDebit - totalCredit)}`,
                variant: "danger"
            });
        } else if (totalCredit > totalDebit) {
            setBalanceStatus({
                message: `Kredit är mer än debet med ${formatCurrency(totalCredit - totalDebit)}`,
                variant: "danger"
            });
        } else {
            setBalanceStatus({
                message: "",
                variant: "secondary"
            });
        }
    };

    // File handling functions
    const handleFileUpload = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        setUploadingFiles(true);
        const newAttachments = [];

        try {
            for (const file of files) {
                // Validate file size (10MB limit)
                if (file.size > 10 * 1024 * 1024) {
                    toast.error(`Filen "${file.name}" är för stor. Maximal filstorlek är 10MB.`);
                    continue;
                }

                // Validate file type
                const allowedTypes = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx', '.xlsx', '.xls', '.txt'];
                const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
                if (!allowedTypes.includes(fileExtension)) {
                    toast.error(`Filtypen för "${file.name}" stöds inte.`);
                    continue;
                }

                // Create form data for upload
                const formData = new FormData();
                formData.append('file', file);

                try {

                    const uploadResponse = await axios.post(
                        getApiUrl(`${companyId}/accounting/verifications/attachments`),
                        formData,
                        {
                            withCredentials: true,
                            headers: {
                                'Content-Type': 'multipart/form-data'
                            }
                        }
                    );

                    if (uploadResponse.data.success) {
                        newAttachments.push({
                            id: uploadResponse.data.attachment.attachmentId, // Use server-generated attachment ID
                            attachmentId: uploadResponse.data.attachment.attachmentId,
                            blobName: uploadResponse.data.attachment.blobName,
                            fileUrl: uploadResponse.data.attachment.fileUrl,
                            filename: uploadResponse.data.attachment.fileName,
                            original_filename: file.name,
                            file_size: uploadResponse.data.fileSize,
                            file_type: uploadResponse.data.fileType,
                            upload_date: new Date().toISOString()
                        });
                    } else {
                        throw new Error(uploadResponse.data.message || "Upload failed");
                    }
                } catch (uploadError) {
                    toast.error(`Det gick inte att ladda upp "${file.name}". Försök igen.`);
                }
            }

            if (newAttachments.length > 0) {
                setAttachments(prev => [...prev, ...newAttachments]);
                toast.success(`${newAttachments.length} fil(er) har laddats upp.`);
            }
        } catch (error) {
            setError('Det gick inte att ladda upp filerna. Försök igen senare.');
        } finally {
            setUploadingFiles(false);
            // Reset file input
            event.target.value = '';
        }
    };

    const removeAttachment = (attachmentId) => {
        swal({
            title: "Ta bort bilaga?",
            text: "Är du säker på att du vill ta bort denna bilaga?",
            icon: "warning",
            buttons: ["Avbryt", "Ta bort"],
            dangerMode: true,
        }).then((willDelete) => {
            if (willDelete) {
                // Remove from local state using attachmentId (which is now the same as id)
                setAttachments(prev => prev.filter(att => att.attachmentId !== attachmentId));

                // Clear any active preview if it was for this attachment
                if (hoveredAttachment?.attachmentId === attachmentId) {
                    handleAttachmentLeave();
                }
            }
        });
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Preview functionality functions
    const canPreviewFile = (fileName) => {
        if (!fileName) return false;
        const extension = fileName.toLowerCase().split('.').pop();
        return ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'txt', 'csv', 'json', 'xml'].includes(extension);
    };

    const fetchAttachmentBlob = async (attachment) => {
        try {
            const response = await axios.get(
                getApiUrl(`${companyId}/attachments/${attachment.attachmentId}`),
                {
                    withCredentials: true,
                    responseType: 'blob',
                    timeout: 10000
                }
            );
            return URL.createObjectURL(response.data);
        } catch (error) {
            toast.error('Det gick inte att hämta bilagan för förhandsgranskning.');
            throw error;
        }
    };

    const fetchTextContent = async (attachment) => {
        try {
            const response = await axios.get(
                getApiUrl(`${companyId}/attachments/${attachment.attachmentId}`),
                {
                    withCredentials: true,
                    responseType: 'text',
                    timeout: 10000
                }
            );
            return response.data;
        } catch (error) {
            toast.error('Det gick inte att hämta textinnehållet för bilagan.');
            throw error;
        }
    };

    const handleAttachmentHover = async (attachment, event) => {
        if (!canPreviewFile(attachment.original_filename)) {
            return;
        }

        setPreviewLoading(true);
        setHoveredAttachment(attachment);

        const rect = event.currentTarget.getBoundingClientRect();
        const x = rect.right + 10;
        const y = rect.top;

        const isImage = /\.(jpg|jpeg|png|gif)$/i.test(attachment.original_filename);
        const isPdf = /\.pdf$/i.test(attachment.original_filename);
        const isText = /\.(txt|csv|json|xml)$/i.test(attachment.original_filename);

        try {
            let previewContent;
            let blobUrl = null;

            if (isImage || isPdf) {
                blobUrl = await fetchAttachmentBlob(attachment);
                if (isImage) {
                    previewContent = (
                        <img
                            src={blobUrl}
                            alt={attachment.original_filename}
                            style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain'
                            }}
                            onLoad={() => setPreviewLoading(false)}
                            onError={() => {
                                setPreviewLoading(false);
                                console.error('Image failed to load');
                            }}
                        />
                    );
                } else if (isPdf) {
                    previewContent = (
                        <iframe
                            src={`${blobUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                            title={attachment.original_filename}
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none'
                            }}
                            onLoad={() => setPreviewLoading(false)}
                            onError={() => {
                                setPreviewLoading(false);
                                console.error('PDF failed to load');
                            }}
                        />
                    );
                }
            } else if (isText) {
                const textContent = await fetchTextContent(attachment);
                previewContent = (
                    <pre style={{
                        fontSize: '12px',
                        margin: 0,
                        padding: '10px',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        backgroundColor: '#f8f9fa'
                    }}>
                        {textContent.length > 1000 ? textContent.substring(0, 1000) + '...' : textContent}
                    </pre>
                );
                setPreviewLoading(false);
            }

            const previewElement = (
                <div
                    style={{
                        position: 'fixed',
                        left: `${x}px`,
                        top: `${y}px`,
                        width: '450px',
                        height: '500px',
                        backgroundColor: '#fff',
                        border: '2px solid #007bff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        zIndex: 9999,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    {/* Header */}
                    <div style={{
                        padding: '8px 12px',
                        backgroundColor: '#007bff',
                        color: '#fff',
                        fontSize: '12px',
                        fontWeight: 'bold'
                    }}>
                        {attachment.original_filename}
                    </div>

                    {/* Content */}
                    <div style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden'
                    }}>
                        {previewLoading ? (
                            <div style={{ textAlign: 'center', color: '#666' }}>
                                <Spinner animation="border" size="sm" />
                                <div style={{ marginTop: '8px', fontSize: '12px' }}>Laddar...</div>
                            </div>
                        ) : (
                            previewContent
                        )}
                    </div>
                </div>
            );

            setPreviewWindow({
                element: previewElement,
                blobUrl: blobUrl
            });

        } catch (error) {
            setPreviewLoading(false);
        }
    };

    const handleAttachmentLeave = () => {
        if (previewWindow?.blobUrl) {
            URL.revokeObjectURL(previewWindow.blobUrl);
        }
        setPreviewWindow(null);
        setHoveredAttachment(null);
        setPreviewLoading(false);
    };

    const handleAddRow = () => {
        setManualRows([...manualRows, {
            id: Date.now(),
            account_id: "",
            account_number: "",
            account_name: "",
            debit: "",
            credit: ""
        }]);
    };

    const handleRemoveRow = (index) => {
        if (manualRows.length > 2) { // Keep minimum 2 rows
            setManualRows(manualRows.filter((row, rowIndex) => rowIndex !== index));
        } else {
            toast.info("Du måste ha minst två transaktioner för ett verifikat.");
        }
    };

    const handleRowChange = (index, field, value) => {
        const newRows = [...manualRows];
        if (field === "account_id") {
            const selectedAccount = accounts.find((option) => option.value === value);
            if (selectedAccount) {
                newRows[index] = {
                    ...newRows[index],
                    account_id: selectedAccount.value,
                    account_number: selectedAccount.accountNumber,
                    account_name: selectedAccount.accountName,
                };
            }
        } else if (field === "debit") {
            // If debit is entered, clear credit
            newRows[index] = {
                ...newRows[index],
                debit: value,
                credit: value ? "" : newRows[index].credit
            };
        } else if (field === "credit") {
            // If credit is entered, clear debit
            newRows[index] = {
                ...newRows[index],
                credit: value,
                debit: value ? "" : newRows[index].debit
            };
        } else {
            newRows[index][field] = value;
        }
        setManualRows(newRows);
    };

    const handleSave = () => {
        if (!isFormValid()) {
            toast.error("Kontrollera att alla fält är ifyllda och att debet och kredit balanserar");
            return;
        }
        handleAddVerification();
    };

    const handleAbort = () => {
        // Show confirmation if user has made changes
        const hasChanges = description || reference || comments || attachments.length > 0 ||
            manualRows.some(row => row.account_id || row.debit || row.credit);

        if (hasChanges) {
            swal({
                title: "Är du säker?",
                text: "Ändringar du gjort kommer att gå förlorade",
                icon: "warning",
                buttons: ["Avbryt", "Ja, avbryt"],
                dangerMode: true,
            }).then((willDiscard) => {
                if (willDiscard) {
                    navigateBack();
                }
            });
        } else {
            navigateBack();
        }
    };

    const navigateBack = () => {
        // Navigate back to the verification list
        history.push(`/${companyId}/verifications`);
    };

    const handleAddVerification = async () => {
        setSavingVerification(true);
        setError(null);

        try {
            const createVerificationApiUrl = getApiUrl(`${companyId}/verifications`);
            const theDate = date.toISOString().split('T')[0];

            // Format transactions
            const transactions = manualRows.map(row => {
                // Determine transaction type based on debit or credit
                const transaction_type = row.debit ? 'debit' : 'credit';
                const amount = parseFloat(row.debit || row.credit);

                return {
                    account_id: row.account_id,
                    transaction_type,
                    amount
                };
            });

            // Format attachments for API
            const formattedAttachments = attachments.map(att => ({
                attachmentId: att.attachmentId,
                blobName: att.blobName,
                fileUrl: att.fileUrl,
                file_name: att.original_filename,
                file_type: att.file_type,
                file_size: att.file_size,
                blob_name: att.blobName,
                file_url: att.fileUrl
            }));

            const response = await axios.post(
                createVerificationApiUrl,
                {
                    date: theDate,
                    description,
                    reference: reference || null,
                    notes: comments || null,
                    series: 'V',
                    transactions,
                    attachments: formattedAttachments
                },
                { withCredentials: true }
            );

            if (response.data.success) {
                toast.success("Verifikatet har skapats och lagts till i listan");
                history.push(`/${companyId}/verifications?updated=true`);
            } else {
                throw new Error(response.data.message || "Det gick inte att skapa verifikatet");
            }
        } catch (error) {
            setError(error.response?.data?.message || "Det gick inte att skapa verifikatet. Försök igen senare.");
        } finally {
            setSavingVerification(false);
        }
    };

    // Calculate totals for display
    const totalDebit = manualRows.reduce((sum, row) => sum + parseFloat(row.debit || 0), 0);
    const totalCredit = manualRows.reduce((sum, row) => sum + parseFloat(row.credit || 0), 0);
    const isBalanced = Math.abs(totalDebit - totalCredit) <= 0.01;

    if (loading && accounts.length === 0) {
        return (
            <div className="container-fluid">
                <PageTitle motherMenu="Bokföring" activeMenu="Skapa verifikat" />
                <div className="text-center py-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Laddar...</span>
                    </Spinner>
                    <p className="mt-2">Laddar kontoplan...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid" style={{ maxWidth: '1400px' }}>
            <style>{`
                .verification-section {
                    background: #f8fafc;
                    border-radius: 12px;
                    padding: 1.5rem;
                    margin-bottom: 1.5rem;
                    border: 1px solid #e2e8f0;
                }
                
                .section-icon {
                    width: 24px;
                    height: 24px;
                    color: #64748b;
                }
                
                .page-header {
                    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                    color: white;
                    border-radius: 12px;
                    padding: 2rem;
                    margin-bottom: 2rem;
                }
                
                .verification-table {
                    background: white;
                    border-radius: 8px;
                    overflow: hidden;
                    border: 1px solid #e2e8f0;
                }
                
                .table-header {
                    background: #f1f5f9;
                    border-bottom: 1px solid #e2e8f0;
                }
                
                .amount-cell {
                    font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
                    font-size: 0.875rem;
                }
                
                .balance-indicator {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1rem;
                    border-radius: 8px;
                    font-weight: 500;
                    font-size: 0.875rem;
                    margin-bottom: 1rem;
                }
                
                .balance-good {
                    background: #dcfce7;
                    color: #166534;
                    border: 1px solid #bbf7d0;
                }
                
                .balance-bad {
                    background: #fef2f2;
                    color: #dc2626;
                    border: 1px solid #fecaca;
                }
                
                .action-button {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1.5rem;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #374151;
                    text-decoration: none;
                    transition: all 0.2s ease;
                    font-size: 0.875rem;
                    font-weight: 500;
                    cursor: pointer;
                }
                
                .action-button:hover:not(:disabled) {
                    transform: translateY(-1px);
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                }
                
                .action-button:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
                
                .action-button.primary {
                    background: var(--bs-primary);
                    color: white;
                    border-color: var(--bs-primary);
                }
                
                .action-button.primary:hover:not(:disabled) {
                    background: var(--bs-primary);
                    border-color: var(--bs-primary);
                    color: white;
                }
                
                .action-button.secondary {
                    background: #6b7280;
                    color: white;
                    border-color: #6b7280;
                }
                
                .action-button.secondary:hover:not(:disabled) {
                    background: #4b5563;
                    border-color: #4b5563;
                    color: white;
                }
                
                .transaction-row {
                    transition: all 0.2s ease;
                }
                
                .transaction-row:hover {
                    background: #f8fafc;
                }
                
                .add-row-button {
                    color: var(--bs-primary);
                    border: 2px dashed #b8cdbbff;
                    background: transparent;
                    padding: 1rem;
                    text-align: center;
                    border-radius: 8px;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                
                .add-row-button:hover {
                    border-color: var(--bs-primary);
                    background: #f8fafc;
                }
                
                .comment-section {
                    background: white;
                    border-radius: 8px;
                    padding: 1rem;
                    border: 1px solid #e2e8f0;
                    margin-bottom: 1rem;
                }
                
                .attachment-item {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 0.75rem;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    margin-bottom: 0.5rem;
                }
                
                .attachment-info {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }
                
                /* New File Item Styles */
                .file-item {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    width: 80px;
                    padding: 8px;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    transition: all 0.2s ease;
                    cursor: pointer;
                    position: relative;
                }
                
                .file-item:hover {
                    border-color: var(--bs-primary);
                    background: #f8fafc;
                    transform: translateY(-1px);
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                }
                
                .file-upload-item {
                    border: 2px dashed #cbd5e1;
                    background: #f8fafc;
                }
                
                .file-upload-item:hover {
                    border-color: var(--bs-primary);
                    background: white;
                }
                
                .file-upload-label {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    width: 100%;
                    cursor: pointer;
                    text-decoration: none;
                    color: inherit;
                }
                
                .file-icon-container {
                    position: relative;
                    margin-bottom: 4px;
                }
                
                .file-icon {
                    color: #64748b;
                }
                
                .upload-icon {
                    color: var(--bs-primary);
                }
                
                .upload-spinner {
                    color: var(--bs-primary);
                }
                
                .file-remove-btn {
                    position: absolute;
                    top: -6px;
                    right: -6px;
                    background: #ef4444;
                    color: white;
                    border: none;
                    border-radius: 50%;
                    width: 18px;
                    height: 18px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 10px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    opacity: 0;
                }
                
                .file-item:hover .file-remove-btn {
                    opacity: 1;
                }
                
                .file-remove-btn:hover {
                    background: #dc2626;
                    transform: scale(1.1);
                }
                
                .file-info {
                    text-align: center;
                    width: 100%;
                }
                
                .file-name {
                    font-size: 10px;
                    font-weight: 500;
                    color: #374151;
                    line-height: 1.2;
                    margin-bottom: 2px;
                    word-break: break-word;
                }
                
                .file-size {
                    font-size: 9px;
                    color: #6b7280;
                    line-height: 1;
                }
                
                .file-upload-item .file-name {
                    color: var(--bs-primary);
                }
                
                .file-upload-area {
                    border: 2px dashed #cbd5e1;
                    border-radius: 8px;
                    padding: 2rem;
                    text-align: center;
                    background: #f8fafc;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                
                .file-upload-area:hover {
                    border-color: var(--bs-primary);
                    background: white;
                }
                
                .file-upload-area.dragover {
                    border-color: var(--bs-primary);
                    background: white;
                }
                
                /* Responsive adjustments */
                @media (max-width: 768px) {
                    .verification-section {
                        padding: 1rem;
                    }
                    
                    .page-header {
                        padding: 1.5rem;
                    }
                    
                    .verification-table {
                        overflow-x: auto;
                    }
                    
                    .file-upload-area {
                        padding: 1rem;
                    }
                }
            `}</style>

            <PageTitle motherMenu="Bokföring" activeMenu="Skapa verifikat" />

            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}

            <Row>
                {/* Main Content */}
                <Col lg={12}>
                    <div className="verification-section">
                        <div className="d-flex align-items-center mb-4">
                            <FileText className="section-icon me-2" />
                            <h5 className="mb-0 fw-medium">Verifikatsinformation</h5>
                        </div>

                        {/* Basic Information */}
                        <Row className="mb-4">
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label className="fw-medium">
                                        <Calendar size={16} className="me-2" />
                                        Datum <span className="text-danger">*</span>
                                    </Form.Label>
                                    <DatePicker
                                        selected={date}
                                        onChange={setDate}
                                        dateFormat="yyyy-MM-dd"
                                        className="form-control"
                                        disabled={savingVerification}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label className="fw-medium">
                                        Referens
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={reference}
                                        onChange={(e) => setReference(e.target.value)}
                                        placeholder="Fakturanummer, kvittonummer, etc."
                                        disabled={savingVerification}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label className="fw-medium">
                                        <FileText size={16} className="me-2" />
                                        Beskrivning <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        placeholder="Ange beskrivning"
                                        disabled={savingVerification}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Transactions Table */}
                        <div className="d-flex align-items-center mb-3">
                            <Calculator className="section-icon me-2" />
                            <h6 className="mb-0 fw-medium">Transaktioner</h6>
                        </div>

                        <div className="verification-table">
                            <div className="table-responsive">
                                <table className="table mb-0">
                                    <thead className="table-header">
                                        <tr>
                                            <th className="border-0 py-3 px-4 fw-medium text-muted">Konto</th>
                                            <th className="border-0 py-3 px-4 fw-medium text-muted text-end">Debet (SEK)</th>
                                            <th className="border-0 py-3 px-4 fw-medium text-muted text-end">Kredit (SEK)</th>
                                            <th className="border-0 py-3 px-4 fw-medium text-muted" style={{ width: '50px' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {manualRows.map((row, index) => (
                                            <tr key={row.id} className="transaction-row">
                                                <td className="py-3 px-4 border-0">
                                                    <Select
                                                        options={accounts}
                                                        value={accounts.find((option) => option.value === row.account_id)}
                                                        onChange={(selectedOption) =>
                                                            handleRowChange(index, "account_id", selectedOption ? selectedOption.value : "")
                                                        }
                                                        isDisabled={savingVerification}
                                                        placeholder="Välj konto..."
                                                        styles={{
                                                            control: (base) => ({
                                                                ...base,
                                                                minWidth: "250px",
                                                                border: '1px solid #e2e8f0',
                                                                borderRadius: '6px'
                                                            })
                                                        }}
                                                    />
                                                </td>
                                                <td className="py-3 px-4 border-0">
                                                    <Form.Control
                                                        type="number"
                                                        value={row.debit}
                                                        onChange={(e) => handleRowChange(index, "debit", e.target.value)}
                                                        placeholder="0.00"
                                                        step="0.01"
                                                        min="0"
                                                        disabled={savingVerification || parseFloat(row.credit) > 0}
                                                        className="amount-cell text-end"
                                                    />
                                                </td>
                                                <td className="py-3 px-4 border-0">
                                                    <Form.Control
                                                        type="number"
                                                        value={row.credit}
                                                        onChange={(e) => handleRowChange(index, "credit", e.target.value)}
                                                        placeholder="0.00"
                                                        step="0.01"
                                                        min="0"
                                                        disabled={savingVerification || parseFloat(row.debit) > 0}
                                                        className="amount-cell text-end"
                                                    />
                                                </td>
                                                <td className="py-3 px-4 border-0 text-center">
                                                    {manualRows.length > 2 && (
                                                        <button
                                                            className="btn btn-link text-danger p-1"
                                                            onClick={() => handleRemoveRow(index)}
                                                            disabled={savingVerification}
                                                            title="Ta bort transaktion"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="border-top bg-light">
                                        <tr>
                                            <td className="py-3 px-4 fw-bold">Summa</td>
                                            <td className="py-3 px-4 text-end fw-bold amount-cell text-success">
                                                {formatCurrency(totalDebit)}
                                            </td>
                                            <td className="py-3 px-4 text-end fw-bold amount-cell text-primary">
                                                {formatCurrency(totalCredit)}
                                            </td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>

                        {/* Add Row Button */}
                        <div
                            className="add-row-button mt-3"
                            onClick={handleAddRow}
                            style={{ cursor: savingVerification ? 'not-allowed' : 'pointer' }}
                        >
                            <Plus size={16} className="me-2" />
                            Lägg till transaktion
                        </div>

                        {/* Comments Section - Moved after transactions */}
                        <div className="mb-4 mt-4">
                            <div className="d-flex align-items-center mb-3">
                                <MessageSquare className="section-icon me-2" />
                                <h6 className="mb-0 fw-medium">Kommentarer</h6>
                                <small className="text-muted ms-2">(Valfritt)</small>
                            </div>
                            <div className="comment-section">
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={comments}
                                    onChange={(e) => setComments(e.target.value)}
                                    placeholder="Lägg till kommentarer för intern användning, förklaringar eller noteringar som kan vara till hjälp senare..."
                                    disabled={savingVerification}
                                    style={{ border: 'none', background: 'transparent' }}
                                />
                            </div>
                        </div>

                        {/* Attachments Section - Moved after transactions */}
                        <div className="mb-4">
                            <div className="d-flex align-items-center mb-3">
                                <Paperclip className="section-icon me-2" />
                                <h6 className="mb-0 fw-medium">Bilagor</h6>
                                <small className="text-muted ms-2">(Valfritt)</small>
                            </div>

                            {/* Files Display Area */}
                            <div className="d-flex flex-wrap gap-2 align-items-start">
                                {/* Uploaded Files */}
                                {attachments.map((attachment) => (
                                    <div
                                        key={attachment.id}
                                        className="file-item"
                                        style={{
                                            cursor: canPreviewFile(attachment.original_filename) ? 'pointer' : 'default',
                                            transition: 'transform 0.2s',
                                            position: 'relative'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (canPreviewFile(attachment.original_filename)) {
                                                handleAttachmentHover(attachment, e);
                                                e.currentTarget.style.transform = 'scale(1.05)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            handleAttachmentLeave();
                                            e.currentTarget.style.transform = 'scale(1)';
                                        }}
                                    >
                                        <div className="file-icon-container">
                                            <File size={24} className="file-icon" />
                                            <button
                                                className="file-remove-btn"
                                                onClick={() => removeAttachment(attachment.id)}
                                                disabled={savingVerification}
                                                title="Ta bort bilaga"
                                            >
                                                <X size={12} />
                                            </button>
                                        </div>
                                        <div className="file-info">
                                            <div className="file-name" title={attachment.original_filename}>
                                                {attachment.original_filename.length > 12
                                                    ? attachment.original_filename.substring(0, 12) + '...'
                                                    : attachment.original_filename}
                                            </div>
                                            <div className="file-size">
                                                {formatFileSize(attachment.file_size)}
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {/* Upload Area */}
                                <div className="file-item file-upload-item">
                                    <input
                                        type="file"
                                        id="file-upload"
                                        multiple
                                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xlsx,.xls,.txt"
                                        onChange={handleFileUpload}
                                        style={{ display: 'none' }}
                                        disabled={uploadingFiles || savingVerification}
                                    />
                                    <label htmlFor="file-upload" className="file-upload-label">
                                        <div className="file-icon-container">
                                            {uploadingFiles ? (
                                                <Spinner animation="border" size="sm" className="upload-spinner" />
                                            ) : (
                                                <Plus size={24} className="upload-icon" />
                                            )}
                                        </div>
                                        <div className="file-info">
                                            <div className="file-name">
                                                {uploadingFiles ? 'Laddar...' : 'Ladda upp'}
                                            </div>
                                            <div className="file-size">
                                                Max 10MB
                                            </div>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {/* Help text */}
                            <div className="small text-muted mt-2">
                                Stödda filtyper: PDF, JPG, PNG, DOC, DOCX, XLS, XLSX, TXT
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="d-flex gap-3 mt-4 pt-3 border-top">
                            <button
                                className={`action-button primary ${!isFormValid() || savingVerification ? 'disabled' : ''}`}
                                onClick={handleSave}
                                disabled={!isFormValid() || savingVerification}
                                title={!areTransactionsBalanced() ? "Transaktioner måste balansera för att spara" : ""}
                            >
                                {savingVerification ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} spin />
                                        Skapar verifikat...
                                    </>
                                ) : (
                                    <>
                                        <Save size={16} />
                                        Skapa Verifikat
                                    </>
                                )}
                            </button>

                            <button
                                className="action-button secondary"
                                onClick={handleAbort}
                                disabled={savingVerification}
                            >
                                <X size={16} />
                                Avbryt
                            </button>
                        </div>
                    </div>
                </Col>

                {/* Right Column: Information and Balance Status */}
                {/*<Col lg={4}>
                    <div className="verification-section">
                        <div className="d-flex align-items-center mb-3">
                            <CheckCircle className="section-icon me-2" />
                            <h6 className="mb-0 fw-medium">Balansering</h6>
                        </div>

                        <div className={`px-3 py-2 rounded ${isBalanced ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`}>
                            {isBalanced ? (
                                <div className="d-flex align-items-center gap-2">
                                    <CheckCircle size={16} />
                                    <span>Debet och kredit balanserar</span>
                                </div>
                            ) : (
                                <div className="d-flex align-items-center gap-2">
                                    <AlertTriangle size={16} />
                                    <span>Differens: {formatCurrency(Math.abs(totalDebit - totalCredit))}</span>
                                </div>
                            )}
                        </div>

                        <div className="mt-3">
                            <div className="d-flex justify-content-between mb-1">
                                <span className="small text-muted">Total Debet:</span>
                                <span className="small fw-medium text-success">{formatCurrency(totalDebit)}</span>
                            </div>
                            <div className="d-flex justify-content-between">
                                <span className="small text-muted">Total Kredit:</span>
                                <span className="small fw-medium text-primary">{formatCurrency(totalCredit)}</span>
                            </div>
                        </div>
                    </div>

                    <div className="verification-section">
                        <div className="d-flex align-items-center mb-3">
                            <Info className="section-icon me-2" />
                            <h6 className="mb-0 fw-medium">Information</h6>
                        </div>

                        <div className="small text-muted">
                            <p>Ett verifikat måste alltid ha debet och kredit i balans. Följ dessa steg:</p>
                            <ol className="ps-3 mb-3">
                                <li className="mb-1">Fyll i grunduppgifter som datum och beskrivning</li>
                                <li className="mb-1">Välj konton för alla transaktioner</li>
                                <li className="mb-1">Ange belopp för debet eller kredit</li>
                                <li className="mb-1">Lägg till kommentarer och bilagor vid behov</li>
                                <li className="mb-1">Kontrollera att summan balanserar</li>
                            </ol>
                        </div>
                    </div>

                    <div className="verification-section">
                        <div className="d-flex align-items-center mb-3">
                            <Calculator className="section-icon me-2" />
                            <h6 className="mb-0 fw-medium">Bokföringsregler</h6>
                        </div>

                        <div className="table-responsive">
                            <table className="table table-sm">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="small">Debet</th>
                                        <th className="small">Kredit</th>
                                    </tr>
                                </thead>
                                <tbody className="small">
                                    <tr>
                                        <td>Tillgångar <span className="text-muted">(ökar)</span></td>
                                        <td>Tillgångar <span className="text-muted">(minskar)</span></td>
                                    </tr>
                                    <tr>
                                        <td>Skulder <span className="text-muted">(minskar)</span></td>
                                        <td>Skulder <span className="text-muted">(ökar)</span></td>
                                    </tr>
                                    <tr>
                                        <td>Kostnader</td>
                                        <td>Intäkter</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Col>*/}
            </Row>

            {/* Preview Window */}
            {previewWindow && previewWindow.element}
        </div>
    );
};

export default CreateVerification;
